import React from "react";
import { useCallback, useState, useEffect } from "react";
import './ride.css';
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark, faTrashCan, faPlay, faCircleCheck, faCircleXmark, faCheck, faXmark, faFlag, faCar, faCreditCard, faPenToSquare, faRotate } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import toast from 'react-hot-toast';
import RouteMap from '../routeMap/RouteMap';
import Chat from '../chat/Chat';
import { joinRideRoom, leaveRideRoom, onSocketEvent, offSocketEvent } from '../../services/socket';
import '../payment/payment.css';
import useTour from "../../tour/useTour";
import rideSteps from "../../tour/steps/rideSteps";
import TourButton from "../tourButton/TourButton";
import { hapticLight, hapticSuccess, hapticWarning, hapticHeavy } from "../../services/haptics";


const Ride = ( { isLoggedIn, loginUser, onLogout } ) => {
  const { rideId } = useParams();
  const [rideDetails, setRideDetails] = useState(null);
  const [deleteInProcess, setDeleteInProcess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState("spam");
  const [reportDescription, setReportDescription] = useState("");
  const [confirmingCompletion, setConfirmingCompletion] = useState(false);
  const [passengerReviews, setPassengerReviews] = useState({});
  const navigate = useNavigate();

  const rideUrl = `/api/v1/ride/getOneRide/${rideId}`;

  const getSingleRide = useCallback (async () => {
    try {
      const response = await axios.get(rideUrl);
      setRideDetails(response.data.rideWithPosterPassengerInfo);
      setLoading(false);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      }
    }
  }, [rideUrl, onLogout, navigate]);

  useEffect(() => {
    getSingleRide();
    joinRideRoom(rideId);

    const handleRideUpdated = () => {
      getSingleRide();
    };

    onSocketEvent("ride-updated", handleRideUpdated);
    const fallbackInterval = setInterval(getSingleRide, 60000);

    return () => {
      leaveRideRoom(rideId);
      offSocketEvent("ride-updated", handleRideUpdated);
      clearInterval(fallbackInterval);
    };
  }, [getSingleRide, rideId]);

  const { startTour } = useTour("ride", rideSteps, !loading && rideDetails !== null);

  if (!isLoggedIn) {
    navigate('/');
    return null;
  }

  if (loading || deleteInProcess) {
    return (
      <div className="sr-ride">
        <div className="sr-ride__top">
          <div className="sr-ride__top-content">
            <div className="sr-ride__status">
              <div className="sr-ride__spinner" />
              <span className="sr-ride__status-text">{deleteInProcess ? "Deleting ride..." : "Loading..."}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { _id, arrival, departure, route, estimatedDate, isStarted, totalSeats, isOfferOrRequest, fare = {}, likes = [], poster = {}, passengerList = [], departureCoords, arrivalCoords } = rideDetails;

  const isLikedByUser = Array.isArray(likes) && likes.some(id => id === loginUser._id);
  const isPoster = poster._id === loginUser._id;
  const isPassenger = passengerList.some(el => el.userID === loginUser._id);
  const hasVehicle = isOfferOrRequest && rideDetails.vehicle && (rideDetails.vehicle.model || rideDetails.vehicle.color || rideDetails.vehicle.plateNumber);

  const getStatusInfo = () => {
    if (rideDetails.isCompleted) return { label: "Completed", cls: "sr-ride__status-badge--completed" };
    if (isStarted) return { label: "In Transit", cls: "sr-ride__status-badge--started" };
    return { label: "Available", cls: "sr-ride__status-badge--available" };
  };

  const statusInfo = getStatusInfo();

  const onBtnClickLikeRide = async (rideID) => {
    try {
      setRideDetails((prevDetails) => {
        const alreadyLiked = Array.isArray(prevDetails.likes) && prevDetails.likes.some(id => id === loginUser._id);
        return {
          ...prevDetails,
          likes: alreadyLiked
            ? prevDetails.likes.filter(id => id !== loginUser._id)
            : [...(prevDetails.likes || []), loginUser._id],
        };
      });

      const response = await axios.post(`/api/v1/ride/likeRide/${rideID}?_method=PUT`);

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        likes: response.data.isLiked
          ? [...new Set([...(prevDetails.likes || []), loginUser._id])]
          : (prevDetails.likes || []).filter(id => id !== loginUser._id),
      }));
      hapticLight();

    } catch (error) {
      getSingleRide();
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to update like.");
      }
    }
  };

  const handleBookRide = async (rideID) => {
    try {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: totalSeats - 1,
        passengerList: [
          ...passengerList, {
            userID: "Loading",
            name: "Loading...",
            ride: rideID,
          }
        ]
      }));

      const response = await axios.post(`/api/v1/ride/addPassenger/${rideID}`);

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: response.data.updatedTotalSeats,
        passengerList: [
          ...passengerList,
          response.data.newPassenger
        ]
      }));
      setLoading(false);
      hapticSuccess();

    } catch (error) {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: totalSeats + 1,
        passengerList: passengerList.filter(
          (passenger) => passenger.userID !== "Loading"
        ),
      }));

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to book ride.");
      }
    }
  };

  const handleCancelBooking = async (rideID, passengerID) => {
    try {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: totalSeats + 1,
        passengerList: passengerList.filter(
          (passenger) => passenger.userID !== passengerID
        ),
      }));

      const response = await axios.post(`/api/v1/ride/deletePassenger/${rideID}&${passengerID}?_method=PUT`);

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: response.data.updatedTotalSeats,
        passengerList: passengerList.filter(
          (passenger) => passenger.userID !== response.data.deletedPassengerID
        ),
      }));
      setLoading(false);
      hapticWarning();

    } catch (error) {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        totalSeats: totalSeats - 1,
        passengerList: passengerList,
      }));

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to cancel booking.");
      }
    }
  };

  const handleToggleAvailed = async (rideID, passengerID) => {
    try {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        passengerList: passengerList.map(
          (passenger) => passenger.userID === passengerID
          ? {...passenger, isRideAvailed: !passenger.isRideAvailed}
          : passenger
        )
      }));

      const response = await axios.post(`/api/v1/ride/toggleAvailed/${rideID}&${passengerID}?_method=PUT`);

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        passengerList: passengerList.map(
          (passenger) =>  passenger.userID === passengerID
          ? {...passenger, isRideAvailed: response.data.updatedAvailedMark}
          : passenger
        )
      }));
      setLoading(false);
      hapticLight();

    } catch (error) {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        passengerList: passengerList.map(
          (passenger) => passenger.userID === passengerID
          ? {...passenger, isRideAvailed: !passenger.isRideAvailed}
          : passenger
        )
      }));

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to update passenger.");
      }
    }
  };

  const onBtnClickStartRide = async (rideID) => {
    try {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isStarted: true,
      }));

      const response = await axios.post(`/api/v1/ride/startRide/${rideID}?_method=PUT`);

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isStarted: response.data.isStarted,
      }));
      setLoading(false);
      hapticHeavy();

    } catch (error) {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isStarted: false,
      }));

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to start ride.");
      }
    }
  };

  const onBtnClickCompleteRide = async (rideID) => {
    try {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isCompleted: true,
      }));

      const response = await axios.post(`/api/v1/ride/completeRide/${rideID}?_method=PUT`);

      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isCompleted: response.data.isCompleted,
      }));
      hapticSuccess();
    } catch (error) {
      setRideDetails((prevDetails) => ({
        ...prevDetails,
        isCompleted: false,
      }));

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to complete ride.");
      }
    }
  };

  const onBtnClickDeleteRide = async (rideID) => {
    const activePassengers = passengerList.filter(p => !p.isBookingCancelled);
    if (activePassengers.length > 0) {
      const confirmed = window.confirm(
        `This ride has ${activePassengers.length} booked passenger${activePassengers.length > 1 ? 's' : ''}. Cancelling will notify them and affect your reliability score. Continue?`
      );
      if (!confirmed) return;
    }

    try {
      setDeleteInProcess(true);
      await axios.post(`/api/v1/ride/deleteRide/${rideID}?_method=PUT`);
      setDeleteInProcess(false);
      hapticWarning();
      navigate('/profile');
    } catch (error) {
      setDeleteInProcess(false);
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to delete ride.");
      }
    }
  };

  const handleConfirmCompletion = async (confirmed) => {
    try {
      setConfirmingCompletion(true);
      await axios.post(`/api/v1/ride/confirmCompletion/${_id}?_method=PUT`, { confirmed });
      setRideDetails((prev) => ({
        ...prev,
        passengerList: prev.passengerList.map((p) =>
          p.userID === loginUser._id
            ? { ...p, hasConfirmedCompletion: confirmed ? "confirmed" : "disputed" }
            : p
        ),
      }));
      toast.success(confirmed ? "Arrival confirmed!" : "Dispute recorded.");
      confirmed ? hapticSuccess() : hapticWarning();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else {
        toast.error(error.response?.data?.message || "Failed to record response.");
      }
    } finally {
      setConfirmingCompletion(false);
    }
  };


  return (
    <div className="sr-ride">
      {/* ── Dark top section ── */}
      <div className="sr-ride__top">
        <div className="sr-ride__top-content">

          {/* ── Type badge only ── */}
          <div className="sr-ride__hero">
            <span className={`sr-ride__type-badge ${isOfferOrRequest ? 'sr-ride__type-badge--offer' : 'sr-ride__type-badge--request'}`}>
              {isOfferOrRequest ? "Ride Offer" : "Ride Request"}
            </span>
          </div>

        </div>
      </div>

      {/* ── White body section ── */}
      <div className="sr-ride__body">

        {/* ── Combined route + stats card ── */}
        <div className="sr-ride__card" data-tour="ride-info">
          {/* Status strip at top of card */}
          <div className={`sr-ride__status-strip ${statusInfo.cls}`}>
            {statusInfo.label}
          </div>

          {/* Row 1: From → To side by side */}
          <div className="sr-ride__route-row">
            <div className="sr-ride__route-col">
              <span className="sr-ride__route-label">From</span>
              <span className="sr-ride__route-city">{departure}</span>
            </div>
            <div className="sr-ride__route-arrow">
              <svg width="20" height="12" viewBox="0 0 20 12" fill="none">
                <path d="M0 6h17M13 1l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="sr-ride__route-col sr-ride__route-col--to">
              <span className="sr-ride__route-label">To</span>
              <span className="sr-ride__route-city">{arrival}</span>
            </div>
          </div>
          {route && route !== "nil" && (
            <div className="sr-ride__route-via">via {route}</div>
          )}

          {/* Row 2: Stats (3 columns only) */}
          <div className="sr-ride__stats">
            <div className="sr-ride__stat">
              <span className="sr-ride__stat-label">Departure</span>
              <span className="sr-ride__stat-value">
                {new Date(estimatedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                {', '}
                {new Date(estimatedDate).toLocaleTimeString([], { hour: '2-digit', hour12: true, timeZone: 'UTC' })}
              </span>
            </div>

            {isOfferOrRequest && (
              <div className="sr-ride__stat">
                <span className="sr-ride__stat-label">Fare</span>
                <span className="sr-ride__stat-value">{`${fare?.currency || "PKR"} ${fare?.amount ?? "—"}`}</span>
              </div>
            )}

            {isOfferOrRequest && (
              <div className="sr-ride__stat">
                <span className="sr-ride__stat-label">Seats</span>
                <span className="sr-ride__stat-value">{totalSeats}</span>
              </div>
            )}
          </div>

          {/* Poster + Vehicle inside same card */}
          <div className="sr-ride__card-footer">
            <Link to={`/profile/${poster._id}`} className="sr-ride__poster-link">
              <img
                src={poster.avatar}
                alt={poster.userName}
                className="sr-ride__poster-avatar"
              />
              <div className="sr-ride__poster-details">
                <span className="sr-ride__poster-name">{poster.userName}</span>
                {poster.role === 'admin' && (
                  <span className="sr-ride__poster-badge sr-ride__poster-badge--admin">Admin</span>
                )}
                {poster.isApproved && poster.role !== 'admin' && (
                  <span className="sr-ride__poster-badge sr-ride__poster-badge--verified">Admin Verified</span>
                )}
              </div>
            </Link>
            {hasVehicle && (
              <div className="sr-ride__vehicle-info">
                <FontAwesomeIcon icon={faCar} className="sr-ride__vehicle-icon" />
                <span className="sr-ride__vehicle-text">
                  {[rideDetails.vehicle.model, rideDetails.vehicle.color, rideDetails.vehicle.plateNumber].filter(Boolean).join(" \u00B7 ")}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Route Map ── */}
        {departureCoords && arrivalCoords && departureCoords.lat && arrivalCoords.lat && (
          <div className="sr-ride__map-wrapper" data-tour="ride-map">
            <RouteMap
              departureCoords={departureCoords}
              arrivalCoords={arrivalCoords}
              departureName={departure}
              arrivalName={arrival}
            />
          </div>
        )}

        {/* ── Chat ── */}
        <div data-tour="ride-chat">
          <Chat rideId={_id} loginUser={loginUser} />
        </div>

        {/* ── Passengers ── */}
        {isOfferOrRequest && passengerList.length > 0 && (
          <div className="sr-ride__passengers" data-tour="ride-passengers">
            <h3 className="sr-ride__section-title">Passengers</h3>
            <div className="sr-ride__passenger-avatars">
              {passengerList.map((el) => (
                <Link to={`/profile/${el.userID}`} className="sr-ride__passenger-avatar-link" key={el.userID} title={el.name}>
                  <img
                    src={el.avatar}
                    alt={el.name}
                    className="sr-ride__passenger-avatar-img"
                  />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ── Confirm Arrival ── */}
        {rideDetails.isCompleted && !isPoster && isPassenger && (() => {
          const myRecord = passengerList.find(el => el.userID === loginUser._id);
          if (!myRecord) return null;
          if (myRecord.hasConfirmedCompletion === "pending") {
            return (
              <div className="sr-ride__confirmation">
                <h3 className="sr-ride__section-title">Confirm your arrival</h3>
                <p className="sr-ride__confirmation-text">Did you arrive safely at your destination?</p>
                <div className="sr-ride__confirmation-actions">
                  <button onClick={() => handleConfirmCompletion(true)} className="sr-ride__btn sr-ride__btn--book" disabled={confirmingCompletion}>
                    <FontAwesomeIcon icon={faCheck} /> Yes, Confirmed
                  </button>
                  <button onClick={() => handleConfirmCompletion(false)} className="sr-ride__btn sr-ride__btn--danger" disabled={confirmingCompletion}>
                    <FontAwesomeIcon icon={faXmark} /> No, Dispute
                  </button>
                </div>
              </div>
            );
          }
          if (myRecord.hasConfirmedCompletion === "confirmed") {
            return (
              <div className="sr-ride__confirmation-badge sr-ride__confirmation-badge--confirmed">
                <FontAwesomeIcon icon={faCheck} /> Arrival confirmed
              </div>
            );
          }
          if (myRecord.hasConfirmedCompletion === "disputed") {
            return (
              <div className="sr-ride__confirmation-badge sr-ride__confirmation-badge--disputed">
                <FontAwesomeIcon icon={faXmark} /> You disputed this ride
              </div>
            );
          }
          return null;
        })()}

        {/* ── Review ── */}
        {rideDetails.isCompleted && !isPoster && (
          <div className="sr-ride__review">
            <h3 className="sr-ride__section-title">Rate this ride</h3>
            <div className="sr-ride__review-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`sr-ride__review-star ${star <= reviewRating ? 'sr-ride__review-star--filled' : 'sr-ride__review-star--empty'}`}
                  onClick={() => setReviewRating(star)}
                >
                  ★
                </span>
              ))}
            </div>
            <textarea
              className="sr-ride__textarea"
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder="Write a comment (optional)"
              maxLength={500}
            />
            <button
              className="sr-ride__submit-btn"
              onClick={async () => {
                if (reviewRating === 0) { toast.error("Please select a rating."); return; }
                try {
                  await axios.post("/api/v1/review/create", { rideId: _id, revieweeId: poster._id, rating: reviewRating, comment: reviewComment });
                  toast.success("Review submitted!");
                  setReviewRating(0);
                  setReviewComment("");
                } catch (err) {
                  toast.error(err.response?.data?.message || "Failed to submit review.");
                }
              }}
            >Submit Review</button>
          </div>
        )}

        {/* ── Ride Actions ── */}
        <div className="sr-ride__action-bar" data-tour="ride-actions">
          <div className="sr-ride__action-bar-inner">
            {!isPoster && (
              <>
                {isOfferOrRequest && totalSeats > 0 && !isPassenger && !isStarted && !rideDetails.isCompleted &&
                  <button onClick={() => handleBookRide(_id)} className="sr-ride__btn sr-ride__btn--book">
                    <FontAwesomeIcon icon={faCircleCheck}/> Book
                  </button>
                }

                {isOfferOrRequest && isPassenger && !isStarted &&
                  <button onClick={() => handleCancelBooking(_id, loginUser._id)} className="sr-ride__btn sr-ride__btn--danger">
                    <FontAwesomeIcon icon={faCircleXmark}/> Unbook
                  </button>
                }

                {isOfferOrRequest && rideDetails.isCompleted && isPassenger && (
                  (() => {
                    const myRecord = passengerList.find(el => el.userID === loginUser._id);
                    if (myRecord && myRecord.paymentStatus === "paid") {
                      return <span className="sr__payment-badge sr__payment-badge--paid">Paid</span>;
                    }
                    return (
                      <button disabled className="sr-ride__btn sr-ride__btn--pay">
                        <FontAwesomeIcon icon={faCreditCard}/> Pay Now
                      </button>
                    );
                  })()
                )}
              </>
            )}

            {isPoster && (
              <>
                {!isStarted && (
                  <>
                    <button onClick={() => navigate(`/publish?edit=${_id}`)} className="sr-ride__btn sr-ride__btn--edit">
                      <FontAwesomeIcon icon={faPenToSquare}/> Edit
                    </button>
                    {isOfferOrRequest && (
                      <button onClick={() => onBtnClickStartRide(_id)} className="sr-ride__btn sr-ride__btn--start">
                        <FontAwesomeIcon icon={faPlay}/> Start
                      </button>
                    )}
                    <button onClick={() => onBtnClickDeleteRide(_id)} className="sr-ride__btn sr-ride__btn--danger">
                      <FontAwesomeIcon icon={faTrashCan}/> Delete
                    </button>
                  </>
                )}

                {isStarted && !rideDetails.isCompleted && isOfferOrRequest && (
                  <button onClick={() => onBtnClickCompleteRide(_id)} className="sr-ride__btn sr-ride__btn--book">
                    <FontAwesomeIcon icon={faCircleCheck}/> Complete
                  </button>
                )}

                {rideDetails.isCompleted && (
                  <button onClick={() => navigate(`/publish?repeat=${_id}`)} className="sr-ride__btn sr-ride__btn--edit">
                    <FontAwesomeIcon icon={faRotate}/> Repeat
                  </button>
                )}
              </>
            )}

            <button onClick={() => onBtnClickLikeRide(_id)} className={`sr-ride__btn sr-ride__btn--like ${isLikedByUser ? 'is-liked' : ''}`}>
              {Array.isArray(likes) ? likes.length : 0} <FontAwesomeIcon icon={faBookmark}/>
            </button>

            <button
              onClick={() => {
                const text = `Check out this ride on KamraCarpool!\n${departure} → ${arrival}\n${window.location.href}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
              }}
              className="sr-ride__btn sr-ride__btn--whatsapp"
            >
              <FontAwesomeIcon icon={faWhatsapp}/>
            </button>

            {!isPoster && (
              <button onClick={() => setShowReportForm(!showReportForm)} className="sr-ride__btn sr-ride__btn--report" title="Report">
                <FontAwesomeIcon icon={faFlag}/>
              </button>
            )}
          </div>
        </div>

        {isPoster && rideDetails.isCompleted && isOfferOrRequest && passengerList.length > 0 && (
              <div className="sr-ride__availed-section">
                <h4 className="sr-ride__availed-title">Mark attendance & review</h4>
                <div className="sr-ride__availed-list">
                  {passengerList.map((el) => {
                    const pr = passengerReviews[el.userID] || { rating: 0, comment: "", submitted: false };
                    return (
                      <div className="sr-ride__availed-row sr-ride__availed-row--expanded" key={el.userID}>
                        <div className="sr-ride__availed-row-top">
                          <Link to={`/profile/${el.userID}`} className="sr-ride__availed-user">
                            <img src={el.avatar} alt={el.name} className="sr-ride__availed-avatar" />
                            <span className="sr-ride__availed-name">{el.name}</span>
                          </Link>
                          <button
                            onClick={() => handleToggleAvailed(_id, el.userID)}
                            className={`sr-ride__availed-toggle ${el.isRideAvailed ? 'sr-ride__availed-toggle--yes' : 'sr-ride__availed-toggle--no'}`}
                            title={el.isRideAvailed ? "Showed up" : "Didn't show up"}
                          >
                            <FontAwesomeIcon icon={el.isRideAvailed ? faCheck : faXmark} />
                            {el.isRideAvailed ? " Showed up" : " Didn't show"}
                          </button>
                        </div>
                        {pr.submitted ? (
                          <div className="sr-ride__availed-reviewed">Reviewed ({pr.rating}★)</div>
                        ) : (
                          <div className="sr-ride__availed-review">
                            <div className="sr-ride__review-stars">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                  key={star}
                                  className={`sr-ride__review-star ${star <= pr.rating ? 'sr-ride__review-star--filled' : 'sr-ride__review-star--empty'}`}
                                  onClick={() => setPassengerReviews((prev) => ({ ...prev, [el.userID]: { ...prev[el.userID], rating: star } }))}
                                >★</span>
                              ))}
                            </div>
                            <textarea
                              className="sr-ride__textarea sr-ride__textarea--small"
                              value={pr.comment}
                              onChange={(e) => setPassengerReviews((prev) => ({ ...prev, [el.userID]: { ...prev[el.userID], rating: pr.rating, comment: e.target.value } }))}
                              placeholder="Comment (optional)"
                              maxLength={500}
                            />
                            <button
                              className="sr-ride__submit-btn sr-ride__submit-btn--small"
                              onClick={async () => {
                                if (pr.rating === 0) { toast.error("Please select a rating."); return; }
                                try {
                                  await axios.post("/api/v1/review/create", { rideId: _id, revieweeId: el.userID, rating: pr.rating, comment: pr.comment || "" });
                                  setPassengerReviews((prev) => ({ ...prev, [el.userID]: { ...prev[el.userID], submitted: true } }));
                                  toast.success(`Review for ${el.name} submitted!`);
                                } catch (err) {
                                  toast.error(err.response?.data?.message || "Failed to submit review.");
                                }
                              }}
                            >Submit</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

        {isPoster && !isStarted && (
          <ul className="sr-ride__delete-rules">
            <li>You can only delete rides that haven't started yet</li>
            <li>In-transit or completed rides cannot be deleted</li>
            <li>Booked passengers will be notified if you delete</li>
          </ul>
        )}

        {/* ── Report Form (expands below actions) ── */}
        {!isPoster && showReportForm && (
          <div className="sr-ride__report-form">
            <select className="sr-ride__select" value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
              <option value="spam">Spam</option>
              <option value="harassment">Harassment</option>
              <option value="inappropriate">Inappropriate</option>
              <option value="fraud">Fraud</option>
              <option value="safety">Safety Concern</option>
              <option value="other">Other</option>
            </select>
            <textarea
              className="sr-ride__textarea"
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              placeholder="Describe the issue (optional)"
              maxLength={1000}
            />
            <button
              className="sr-ride__submit-btn"
              onClick={async () => {
                try {
                  await axios.post("/api/v1/report/create", { reportedRide: _id, reportedUser: poster._id, reason: reportReason, description: reportDescription });
                  toast.success("Report submitted. Thank you.");
                  setShowReportForm(false);
                  setReportDescription("");
                } catch (err) {
                  toast.error(err.response?.data?.message || "Failed to submit report.");
                }
              }}
            >Submit Report</button>
          </div>
        )}
      </div>
      <TourButton onClick={startTour} />
    </div>
  );
}

export default Ride;
